import { getSDK } from '../sdk.js'

/**
 * Create a notification for a user.
 * Callable via el.call('createNotification', { ... })
 *
 * @param {object} opts
 * @param {string} opts.userEmail - recipient
 * @param {string} opts.type - 'mention' | 'ticket_assign' | 'calendar_invite' | 'doc_share' | 'system'
 * @param {string} opts.title
 * @param {string} [opts.body]
 * @param {string} [opts.href] - deep link path
 * @param {object} [opts.metadata] - extra payload
 */
export const createNotification = async function (opts) {
  if (!opts?.userEmail || !opts?.type || !opts?.title) return null
  // Don't notify yourself
  const me = this?.state?.root?.currentUserEmail
  if (me && opts.userEmail === me) return null
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.notifications/create
    const result = await getSDK().execute('workspaceProject.notifications', 'create', {
      payload: {
        user_email: opts.userEmail,
        type: opts.type,
        title: opts.title,
        body: opts.body || null,
        href: opts.href || null,
        metadata: opts.metadata || {}
      }
    })
    // The worker's insert returns `[{...row}]` (PostgREST `.insert().select()`
    // convention — same array-wrap createChannel.js / createRoom.js / sendMessage.js
    // all unwrap) rather than a bare row or a `{data}` envelope. This was never
    // unwrapped here, so every call silently returned `null` on the *success*
    // path too (verified live: POST succeeded, row landed in Mongo, but this
    // returned `null`) — nobody had noticed because neither existing caller
    // (chat's mention-notify, calendar's invite-notify) checks the return
    // value. Handle all three shapes so `el.call('createNotification', …)`
    // truthiness checks (e.g. a Ping send confirming before showing "sent")
    // are meaningful.
    const row = Array.isArray(result) ? result[0] : result?.data ?? result
    return row?.id || row?._id || null
  } catch (e) {
    console.error('[createNotification]', e?.message || e)
    return null
  }
}

/**
 * Create notifications for multiple users at once.
 * NOTE: the workspace wrapper currently exposes a single-row /notifications
 * POST. Bulk inserts are issued as N parallel SDK calls until a
 * /notifications/bulk endpoint ships.
 */
export const createNotifications = async function (recipients, baseOpts) {
  if (!recipients?.length || !baseOpts?.type || !baseOpts?.title) return
  const me = this?.state?.root?.currentUserEmail
  const rows = recipients
    .filter((email) => email !== me)
    .map((email) => ({
      user_email: email,
      type: baseOpts.type,
      title: baseOpts.title,
      body: baseOpts.body || null,
      href: baseOpts.href || null,
      metadata: baseOpts.metadata || {}
    }))
  if (!rows.length) return
  try {
    // migrated 2026-04-27 to SDK route workspaceProject.notifications/create (fan-out)
    await Promise.all(
      rows.map((payload) =>
        getSDK()
          .execute('workspaceProject.notifications', 'create', { payload })
          .catch((err) => {
            console.error('[createNotifications]', err?.message || err)
          })
      )
    )
  } catch (e) {
    console.error('[createNotifications]', e?.message || e)
  }
}
